/**
 * Tâche 1424 (Eric 2026-10-01) — Carte d'un sondage dans la messagerie.
 *
 * Le même composant sert dans un canal et dans une conversation privée : un
 * sondage est un message, il n'a pas à savoir où il est affiché.
 *
 * Deux choix d'affichage qui ne vont pas de soi :
 *   • les pourcentages sont calculés sur le nombre de PERSONNES qui ont voté,
 *     pas sur la somme des voix. En choix multiple, la somme dépasse 100 % et
 *     une barre de 140 % n'a aucun sens ;
 *   • on affiche TOUJOURS les réponses à 0 voix. Les masquer ferait croire
 *     qu'elles n'ont jamais été proposées, et c'est précisément l'information
 *     qu'un sondage doit donner.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChartBar, Lock, Plus, EyeSlash, CheckCircle, PushPin, BellRinging, Clock, ClockCounterClockwise } from "@phosphor-icons/react";

import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";

// Tâche 1428 — Cadence du suivi en direct. Trois secondes : au-dessus, un vote
// semble perdu ; en dessous, on interroge le serveur pour rien, car personne ne
// vote dix fois par seconde. La liste des messages se rafraîchit déjà toutes
// les 4 s, mais elle ne suffisait pas : un sondage épinglé vit HORS de cette
// liste, et surtout rien à l'écran ne disait que les chiffres bougeaient tout
// seuls — Eric rechargeait la page pour en être sûr.
const CADENCE_DIRECT_MS = 3000;

// Tâche 1428 — `GET /api/polls/{id}` rend le MESSAGE complet, avec le sondage
// imbriqué ; les actions (voter, épingler) rendent, elles, la vue du sondage.
// Un seul extracteur, pour ne pas avoir à s'en souvenir deux fois.
const vueDuSondage = (donnees) => {
  if (!donnees) return null;
  if (donnees.poll && donnees.poll.options) return donnees.poll;
  if (donnees.options) return donnees;
  return null;
};

export default function PollCard({ message, currentUserId, canAdmin, onChanged, compact = false }) {
  const { lang, tr } = useLang();
  const [vueLive, setVueLive] = useState(null);
  const poll = vueLive || message.poll || {};
  const [occupe, setOccupe] = useState(false);
  const [nouvelleReponse, setNouvelleReponse] = useState("");
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  const [deplie, setDeplie] = useState(false);
  // Réponses dont le compte vient de monter : elles s'éclairent une seconde.
  // Un chiffre qui change sans rien signaler passe inaperçu, et c'est
  // exactement ce qu'il faut voir sur un sondage ouvert.
  const [quiBougent, setQuiBougent] = useState({});
  const comptesPrecedents = useRef(null);
  const minuteurEclat = useRef(null);
  // Tâche 1436 — Historique des votes. Il n'est chargé qu'à l'ouverture : le
  // garder à jour en permanence ferait un appel de plus toutes les 3 secondes
  // pour un panneau que presque personne n'ouvre.
  const [histoOuverte, setHistoOuverte] = useState(false);
  const [histo, setHisto] = useState(null);
  const [histoErreur, setHistoErreur] = useState("");

  const estCreateur = poll.created_by === currentUserId;
  const clos = !!poll.is_closed;
  const total = poll.total_voters || 0;
  const mesChoix = poll.my_option_ids || [];

  // Repartir du message quand le parent en affiche un autre dans la même case.
  useEffect(() => { setVueLive(null); comptesPrecedents.current = null; }, [message.id]);

  const signalerLesHausses = useCallback((vue) => {
    const comptes = {};
    (vue?.options || []).forEach((o) => { comptes[o.id] = o.count; });
    const avant = comptesPrecedents.current;
    comptesPrecedents.current = comptes;
    if (!avant) return;
    const montees = {};
    Object.keys(comptes).forEach((id) => {
      if (avant[id] !== undefined && comptes[id] > avant[id]) montees[id] = true;
    });
    if (!Object.keys(montees).length) return;
    setQuiBougent(montees);
    clearTimeout(minuteurEclat.current);
    minuteurEclat.current = setTimeout(() => setQuiBougent({}), 1200);
  }, []);

  // Suivi en direct : seulement sur un sondage OUVERT, et seulement quand
  // l'onglet est visible. Un onglet laissé de côté pendant la nuit n'a aucune
  // raison d'interroger le serveur mille fois.
  useEffect(() => {
    if (clos || !message.id) return undefined;
    let vivant = true;
    const relire = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const { data } = await axios.get(`${API}/api/polls/${message.id}`, { withCredentials: true });
        if (!vivant) return;
        const vue = vueDuSondage(data);
        if (!vue) return;
        signalerLesHausses(vue);
        setVueLive(vue);
      } catch { /* un sondage supprimé ou un réseau coupé : la carte garde ce qu'elle a */ }
    };
    const id = setInterval(relire, CADENCE_DIRECT_MS);
    return () => { vivant = false; clearInterval(id); clearTimeout(minuteurEclat.current); };
  }, [message.id, clos, signalerLesHausses]);

  const ouvrirHistorique = async () => {
    if (histoOuverte) { setHistoOuverte(false); return; }
    setHistoOuverte(true);
    setHisto(null);
    setHistoErreur("");
    try {
      const { data } = await axios.get(`${API}/api/polls/${message.id}/history`,
                                       { withCredentials: true });
      setHisto(data);
    } catch (err) {
      setHistoErreur(err?.response?.data?.detail
        || tr("Historique indisponible.", "History unavailable."));
    }
  };

  const appeler = async (chemin, corps) => {
    setOccupe(true);
    try {
      const { data } = await axios.post(
        `${API}/api/polls/${message.id}/${chemin}`,
        corps || {},
        { withCredentials: true },
      );
      // La réponse du serveur est la vue la plus fraîche qui existe : on s'en
      // sert tout de suite, sans attendre le prochain tour de suivi.
      const vue = vueDuSondage(data);
      if (vue) setVueLive(vue);
      onChanged?.(data);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || tr("Action impossible.", "Action failed."));
      return null;
    } finally {
      setOccupe(false);
    }
  };

  const voter = (optionId) => {
    if (clos) return;
    let choix;
    if (poll.multiple) {
      choix = mesChoix.includes(optionId)
        ? mesChoix.filter((o) => o !== optionId)
        : [...mesChoix, optionId];
    } else {
      // Recliquer son propre choix le RETIRE : c'est le seul geste qui permet
      // de se rétracter sans devoir voter pour autre chose.
      choix = mesChoix.includes(optionId) ? [] : [optionId];
    }
    appeler("vote", { option_ids: choix });
  };

  const ajouter = async () => {
    const texte = nouvelleReponse.trim();
    if (!texte) return;
    const res = await appeler("options", { text: texte });
    if (res) {
      setNouvelleReponse("");
      setAjoutOuvert(false);
    }
  };

  // Tâche 1425 (choix d'Eric) — Épinglé, pour qui a DÉJÀ voté, le sondage se
  // réduit à une ligne. Le faire disparaître lui retirerait le moyen de
  // changer d'avis ; le laisser entier lui redemanderait ce qu'il a déjà
  // donné. Un clic le rouvre.
  if (compact && poll.i_voted && !deplie) {
    return (
      <button
        type="button"
        onClick={() => setDeplie(true)}
        className="flex w-full items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50/50 px-3 py-2 text-left transition-colors hover:bg-indigo-50"
        data-testid={`poll-collapsed-${message.id}`}
      >
        <CheckCircle size={14} weight="fill" className="shrink-0 text-indigo-600" />
        <span className="truncate text-[13px] font-medium text-zinc-800">{poll.question}</span>
        <span className="ml-auto shrink-0 text-[11px] text-zinc-500">
          {tr(`Tu as voté · ${total} réponse${total === 1 ? "" : "s"}`,
              `You voted · ${total} answer${total === 1 ? "" : "s"}`)}
        </span>
      </button>
    );
  }

  return (
    <div
      className="w-full rounded-xl border border-indigo-200 bg-indigo-50/50 p-3"
      data-testid={`poll-card-${message.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <ChartBar size={16} weight="bold" className="mt-0.5 shrink-0 text-indigo-700" />
          <p className="text-sm font-semibold text-zinc-900 break-words" data-testid={`poll-question-${message.id}`}>
            {poll.question}
          </p>
        </div>
        {clos && (
          <span
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-zinc-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-zinc-600"
            data-testid={`poll-closed-badge-${message.id}`}
          >
            <Lock size={10} weight="bold" />
            {tr("Clos", "Closed")}
          </span>
        )}
      </div>

      <div className="mt-2.5 space-y-1.5">
        {(poll.options || []).map((option) => {
          const choisi = mesChoix.includes(option.id);
          const pct = total > 0 ? Math.round((option.count / total) * 100) : 0;
          const vientDeMonter = !!quiBougent[option.id];
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => voter(option.id)}
              disabled={clos || occupe}
              className={`relative block w-full overflow-hidden rounded-lg border px-2.5 py-1.5 text-left transition-colors ${
                choisi
                  ? "border-indigo-500 bg-white"
                  : "border-zinc-200 bg-white hover:border-indigo-300"
              } ${vientDeMonter ? "ring-2 ring-indigo-400" : ""} ${clos ? "cursor-default" : "cursor-pointer"} disabled:cursor-not-allowed`}
              data-testid={`poll-option-${message.id}-${option.id}`}
              aria-pressed={choisi}
            >
              <span
                className="absolute inset-y-0 left-0 bg-indigo-100 transition-all duration-500"
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              />
              <span className="relative flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5">
                  {choisi && <CheckCircle size={13} weight="fill" className="shrink-0 text-indigo-600" />}
                  <span className="truncate text-[13px] text-zinc-900">{option.text}</span>
                  {option.added_by_name && (
                    <span className="shrink-0 text-[10px] text-zinc-400">
                      {tr(`ajoutée par ${option.added_by_name}`, `added by ${option.added_by_name}`)}
                    </span>
                  )}
                </span>
                <span className={`shrink-0 text-[11px] font-semibold tabular-nums transition-colors ${
                  vientDeMonter ? "text-indigo-700" : "text-zinc-600"
                }`}>
                  {option.count} · {pct} %
                </span>
              </span>
              {/* Les noms ne sont rendus que si le serveur les a envoyés : sur
                  un sondage anonyme, `voter_names` vaut `null` et il n'y a
                  rien à masquer ici — l'anonymat est appliqué au serveur. */}
              {Array.isArray(option.voter_names) && option.voter_names.length > 0 && (
                <span
                  className="relative mt-0.5 block truncate text-[10px] text-zinc-500"
                  data-testid={`poll-voters-${message.id}-${option.id}`}
                >
                  {option.voter_names.join(", ")}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
        <span data-testid={`poll-total-${message.id}`}>
          {total === 1
            ? tr("1 personne a voté", "1 person voted")
            : tr(`${total} personnes ont voté`, `${total} people voted`)}
        </span>
        {poll.multiple && (
          <span data-testid={`poll-multiple-${message.id}`}>
            · {tr("plusieurs réponses permises", "multiple answers allowed")}
          </span>
        )}
        {/* Tâche 1428 — Dire que les chiffres se mettent à jour tout seuls.
            Sans ce repère, un total qui ne bouge pas se confond avec un écran
            figé, et on recharge la page pour vérifier. */}
        {!clos && (
          <span className="inline-flex items-center gap-1 text-indigo-700" data-testid={`poll-live-${message.id}`}>
            · <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-500 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-indigo-600" />
            </span>
            {tr("résultats en direct", "live results")}
          </span>
        )}
        {poll.closes_at && !clos && (
          <span className="inline-flex items-center gap-1" data-testid={`poll-closes-at-${message.id}`}>
            · <Clock size={11} weight="bold" />
            {tr(`clôture le ${String(poll.closes_at).slice(0, 16).replace("T", " à ")}`,
                `closes on ${String(poll.closes_at).slice(0, 16).replace("T", " at ")}`)}
          </span>
        )}
        {poll.anonymous && (
          <span className="inline-flex items-center gap-1" data-testid={`poll-anonymous-${message.id}`}>
            · <EyeSlash size={11} weight="bold" />
            {tr("vote anonyme", "anonymous vote")}
          </span>
        )}
      </div>

      {poll.allow_add_options && !clos && (
        <div className="mt-2">
          {ajoutOuvert ? (
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={nouvelleReponse}
                onChange={(e) => setNouvelleReponse(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ajouter(); } }}
                placeholder={tr("Ta réponse", "Your answer")}
                maxLength={120}
                className="flex-1 rounded-lg border border-zinc-300 px-2 py-1 text-[13px]"
                data-testid={`poll-add-input-${message.id}`}
                autoFocus
              />
              <button
                type="button"
                onClick={ajouter}
                disabled={!nouvelleReponse.trim() || occupe}
                className="rounded-lg bg-indigo-600 px-2.5 py-1 text-[12px] font-semibold text-white disabled:bg-zinc-300"
                data-testid={`poll-add-confirm-${message.id}`}
              >
                {tr("Ajouter", "Add")}
              </button>
              <button
                type="button"
                onClick={() => { setAjoutOuvert(false); setNouvelleReponse(""); }}
                className="px-1.5 py-1 text-[12px] text-zinc-500 hover:text-zinc-800"
                data-testid={`poll-add-cancel-${message.id}`}
              >
                {tr("Annuler", "Cancel")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAjoutOuvert(true)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:underline"
              data-testid={`poll-add-open-${message.id}`}
            >
              <Plus size={11} weight="bold" />
              {tr("Ajouter une réponse", "Add an answer")}
            </button>
          )}
        </div>
      )}

      {(estCreateur || canAdmin) && (
        <div className="mt-2 border-t border-indigo-100 pt-2">
          {/* Tâche 1450 (Eric 2026-06) — Ordre EXACTEMENT comme demandé :
              « Ajouter des réponses », « Épingler », « Relancer »,
              « Historique », « Conserver », « Clore le sondage ».
              ⚠️ « Conserver l'historique des votes » est devenu « Conserver »
              sur sa demande : la case est juste à côté du bouton
              « Historique », donc le mot long répétait son voisin. */}
          {!clos && (
            <label
              className="mr-3 inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-600 cursor-pointer hover:text-zinc-900"
              data-testid={`poll-allow-add-label-${message.id}`}
              title={tr("Laisse les répondants proposer d'autres options. Décocher n'efface AUCUNE réponse déjà ajoutée : elle resterait invisible alors que des votes la désignent.",
                        "Lets respondents propose other options. Unticking deletes NO answer already added: it would become invisible while votes still point at it.")}
            >
              <input
                type="checkbox"
                checked={!!poll.allow_add_options}
                disabled={occupe}
                onChange={() => appeler("add-options-toggle")}
                className="accent-indigo-700"
                data-testid={`poll-allow-add-${message.id}`}
              />
              {tr("Ajouter des réponses", "Allow added answers")}
            </label>
          )}
          {!clos && (
            <button
              type="button"
              onClick={() => appeler("pin")}
              disabled={occupe}
              className="mr-3 inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-600 hover:text-zinc-900 hover:underline disabled:opacity-50"
              data-testid={`poll-toggle-pin-${message.id}`}
              title={tr("Un seul sondage épinglé par canal : épingler celui-ci décroche le précédent.",
                        "One pinned poll per channel: pinning this one unpins the previous.")}
            >
              <PushPin size={11} weight={poll.pinned ? "fill" : "bold"} />
              {poll.pinned ? tr("Décrocher", "Unpin") : tr("Épingler", "Pin")}
            </button>
          )}
          {!clos && (
            <button
              type="button"
              onClick={() => appeler("remind")}
              disabled={occupe}
              className="mr-3 inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-600 hover:text-zinc-900 hover:underline disabled:opacity-50"
              data-testid={`poll-remind-${message.id}`}
              title={tr("Prévient seulement ceux qui n'ont pas encore répondu. Une fois par heure au plus.",
                        "Notifies only those who have not answered yet. Once an hour at most.")}
            >
              <BellRinging size={11} weight="bold" />
              {tr("Relancer", "Remind")}
            </button>
          )}
          <button
            type="button"
            onClick={ouvrirHistorique}
            disabled={occupe}
            className="mr-3 inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-600 hover:text-zinc-900 hover:underline disabled:opacity-50"
            data-testid={`poll-history-open-${message.id}`}
          >
            <ClockCounterClockwise size={11} weight="bold" />
            {tr("Historique", "History")}
            {poll.history_count > 0 && (
              <span className="text-[10px] text-zinc-400">({poll.history_count})</span>
            )}
          </button>
          {/* Tâche 1436 — l'enregistrement des votes. Rien n'est conservé
              avant que la case soit cochée. */}
          <label
            className="mr-3 inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-600 cursor-pointer hover:text-zinc-900"
            data-testid={`poll-history-keep-label-${message.id}`}
            title={poll.anonymous
              ? tr("Conserver l'historique des votes. Sondage anonyme : l'historique ne garde que les totaux dans le temps, jamais les noms.",
                   "Keep vote history. Anonymous poll: the history only keeps totals over time, never names.")
              : tr("Conserver l'historique des votes : chaque vote est gardé avec son horodatage. Rien n'est conservé avant que la case soit cochée.",
                   "Keep vote history: every vote is kept with its timestamp. Nothing is kept before the box is ticked.")}
          >
            <input
              type="checkbox"
              checked={!!poll.keep_history}
              disabled={occupe}
              onChange={async () => {
                await appeler("history-toggle");
                // Le panneau ouvert affiche « l'enregistrement est coupé » :
                // le laisser tel quel après avoir coché afficherait le
                // contraire de ce qui vient de se passer.
                if (histoOuverte) { setHistoOuverte(false); await ouvrirHistorique(); }
              }}
              className="accent-indigo-700"
              data-testid={`poll-history-keep-${message.id}`}
            />
            {tr("Conserver", "Keep")}
          </label>
          <button
            type="button"
            onClick={() => appeler(clos ? "reopen" : "close")}
            disabled={occupe}
            className="text-[11px] font-semibold text-zinc-600 hover:text-zinc-900 hover:underline disabled:opacity-50"
            data-testid={`poll-toggle-close-${message.id}`}
          >
            {clos
              ? tr("Rouvrir le sondage", "Reopen poll")
              : tr("Clore le sondage", "Close poll")}
          </button>
          {clos && (
            <span className="ml-2 text-[10px] text-zinc-400">
              {tr("Les votes déjà donnés sont conservés.", "Existing votes are kept.")}
            </span>
          )}

          {/* Tâche 1436 — Panneau d'historique. Il DIT depuis quand on
              enregistre : sans cette phrase, un historique qui commence au
              milieu du sondage passerait pour des votes perdus. */}
          {histoOuverte && (
            <div
              className="mt-2 border border-indigo-200 bg-white rounded p-2"
              data-testid={`poll-history-panel-${message.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-[11px] font-semibold text-zinc-800">
                  {tr("Historique des votes", "Vote history")}
                </p>
                <button
                  type="button"
                  onClick={() => setHistoOuverte(false)}
                  className="text-[11px] text-zinc-500 hover:text-zinc-900"
                  data-testid={`poll-history-close-${message.id}`}
                >
                  {tr("Fermer", "Close")}
                </button>
              </div>
              {histoErreur && (
                <p className="text-[11px] text-rose-700" data-testid={`poll-history-error-${message.id}`}>
                  {histoErreur}
                </p>
              )}
              {!histoErreur && !histo && (
                <p className="text-[11px] text-zinc-500 italic">{tr("Chargement…", "Loading…")}</p>
              )}
              {histo && (
                <>
                  <p className="text-[10px] text-zinc-500 mb-1" data-testid={`poll-history-since-${message.id}`}>
                    {histo.keep_history
                      ? (histo.history_started_at
                        ? tr(`Enregistrement depuis le ${new Date(histo.history_started_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}. Les votes antérieurs ne sont pas conservés.`,
                             `Recording since ${new Date(histo.history_started_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}. Votes cast before that are not kept.`)
                        : tr("Enregistrement activé.", "Recording enabled."))
                      : tr("L'enregistrement est coupé : cochez la case pour conserver les votes à venir.",
                           "Recording is off: tick the box to keep upcoming votes.")}
                  </p>
                  {histo.anonymous && (
                    <p className="text-[10px] text-amber-800 mb-1" data-testid={`poll-history-anonymous-${message.id}`}>
                      {tr("Sondage anonyme : seuls les totaux sont conservés, aucun nom.",
                          "Anonymous poll: only totals are kept, no names.")}
                    </p>
                  )}
                  {(histo.entries || []).length === 0 ? (
                    <p className="text-[11px] text-zinc-500 italic" data-testid={`poll-history-empty-${message.id}`}>
                      {tr("Aucun vote enregistré pour l'instant.", "No vote recorded yet.")}
                    </p>
                  ) : (
                    <ul className="space-y-1 max-h-48 overflow-y-auto" data-testid={`poll-history-list-${message.id}`}>
                      {[...(histo.entries || [])].reverse().map((e, i) => (
                        <li key={`${e.at}-${i}`} className="text-[11px] text-zinc-700 border-b border-zinc-100 pb-1 last:border-0">
                          <span className="font-mono text-[10px] text-zinc-500">
                            {new Date(e.at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}
                          </span>{" "}
                          {e.voter_name ? (
                            <span>
                              <b>{e.voter_name}</b>{" "}
                              {e.action === "retrait"
                                ? tr("a retiré son vote", "withdrew their vote")
                                : tr("a voté", "voted")}
                              {(e.option_texts || []).length > 0 && (
                                <> : {(e.option_texts || []).join(", ")}</>
                              )}
                            </span>
                          ) : (
                            <span>
                              {e.action === "retrait"
                                ? tr("un vote retiré", "a vote withdrawn")
                                : tr("un vote enregistré", "a vote recorded")}
                            </span>
                          )}
                          <span className="text-zinc-400">
                            {" · "}
                            {(e.totals || []).map((t) => `${t.text} ${t.count}`).join(" · ")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
